import type { DropAnimationFunction } from '@dnd-kit/core';
import { defaultDropAnimationSideEffects } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

const DROP_DURATION_MS = 200;
const DROP_EASING = 'cubic-bezier(0.2, 0, 0, 1)';

export type DropTarget = { left: number; top: number };

// The tile stays dimmed in the grid, so hide it while the overlay lands on top of it.
const hideDroppedTile = defaultDropAnimationSideEffects({
  styles: { active: { opacity: '0' } },
});

/**
 * dnd-kit measures the dragged tile the moment the pointer is released, a render before the
 * consumer's reorder moves that tile into its new slot, so its own drop animation flies the
 * overlay back to the slot the tile came from. Aim at the slot it was dropped on instead.
 */
const dropToSlotAnimation =
  (getTarget: () => DropTarget | null): DropAnimationFunction =>
  ({ active, dragOverlay, transform, ...rest }) => {
    const target = getTarget() ?? active.rect;
    const final = {
      ...transform,
      x: transform.x + (target.left - dragOverlay.rect.left),
      y: transform.y + (target.top - dragOverlay.rect.top),
    };

    const cleanup = hideDroppedTile({ active, dragOverlay, ...rest });
    const animation = dragOverlay.node.animate(
      [
        { transform: CSS.Transform.toString(transform) },
        { transform: CSS.Transform.toString(final) },
      ],
      { duration: DROP_DURATION_MS, easing: DROP_EASING, fill: 'forwards' }
    );

    return animation.finished.then(() => cleanup?.());
  };

export default dropToSlotAnimation;
